import {
	requestAbrirAmbientePreenchimentoEscala,
	requestAbrirAlocacaoOrigemAmbienteEscala,
	requestBuscarGradeEscala,
	requestDetalharVinculoProfissionalEstabelecimento,
	requestDetalharLinhaAmbienteEscala,
} from '../services/escalaRequests';
import { comporAmbienteResumo, adaptarResumoComCards } from './escalaResumo';

const erro = (message) => ({ error: { message } });
const setorId = (linha) => linha?.setor?.id || linha?.setor_id;
const vinculoId = (linha) => Number(linha?.vinculo_profissional?.id || linha?.vinculo_profissional_id || 0) || null;
const profissionalId = (linha) => Number(
	linha?.profissional?.id ||
	linha?.vinculo_profissional?.profissional_id ||
	linha?.profissional_id ||
	0,
) || null;

// O cache só vive nesta tela/sessão. Uma invalidação também impede que uma
// resposta anterior volte a popular o cache depois de uma escrita.
export const criarCacheLeiturasContextuais = () => {
	let geracao = 0;
	const familias = new Map();
	return {
		invalidar() { geracao += 1; familias.clear(); },
		ler(familia, chave, carregar) {
			if (!familias.has(familia)) familias.set(familia, new Map());
			const cache = familias.get(familia);
			if (cache.has(chave)) return cache.get(chave);
			const versao = geracao;
			const leitura = Promise.resolve().then(carregar).then((resultado) => {
				if (resultado?.error || versao !== geracao) cache.delete(chave);
				return resultado;
			}, (falha) => { cache.delete(chave); throw falha; });
			cache.set(chave, leitura);
			return leitura;
		},
	};
};

export const carregarMatrizResumoEscala = async ({ token, scope, contexto, signal, forceRefresh = false }) => {
	if (scope === 'medica') {
		const response = await requestAbrirAmbientePreenchimentoEscala({
			token, scope, signal, forceRefresh,
			contexto: { ...contexto, setor: contexto?.setor || 'TODOS' },
			modo: 'resumo', incluirCards: true,
		});
		if (signal?.aborted) return erro('Leitura cancelada.');
		if (response?.error) return response;
		return adaptarResumoComCards(response);
	}
	const contextoResumo = scope === 'medica'
		? { ...contexto, setor: contexto?.setor || 'TODOS' }
		: contexto;
	const argumentosResumo = {
		token,
		scope,
		contexto: contextoResumo,
		signal,
		modo: 'resumo',
		forceRefresh,
		...(scope === 'medica' ? {} : { incluirProfissionaisSemCards: false }),
	};
	const [ano, mes] = String(contexto?.mes_referencia || '').split('-').map(Number);
	const [resumo, grade] = await Promise.all([
		requestAbrirAmbientePreenchimentoEscala(argumentosResumo),
		requestBuscarGradeEscala({
			token, scope, signal, estabelecimento: contexto?.estabelecimento,
			setor: contexto?.setor || 'TODOS', regime: 'TODOS', ano, mes,
			incluirSugestoesHistoricas: scope === 'medica',
		}),
	]);
	if (signal?.aborted) return erro('Leitura cancelada.');
	if (resumo?.error || grade?.error) return resumo?.error ? resumo : grade;
	let composicao = comporAmbienteResumo({ resumo, grade });
	if (composicao.precisaRecarregarResumo) {
		// Uma escrita concorrente pode criar uma linha entre as duas leituras.
		const atualizado = await requestAbrirAmbientePreenchimentoEscala({
			...argumentosResumo,
			forceRefresh: true,
		});
		if (atualizado?.error) return atualizado;
		composicao = comporAmbienteResumo({ resumo: atualizado, grade });
	}
	if (composicao.precisaRecarregarResumo) {
		return erro('A grade mudou durante o carregamento ou ainda não informa a associação dos registros. Atualize a consulta para continuar.');
	}
	return composicao.ambiente;
};

// A engrenagem possui uma única fonte de leitura. O ambiente detalhado recebe
// a identidade do contrato/profissional e devolve todas as linhas necessárias
// ao modal, tanto na escala médica quanto na de enfermagem.
export const carregarAmbienteDetalhadoProfissionalEscala = async ({
	token,
	scope,
	contexto,
	linha,
	signal,
}) => {
	const contrato = vinculoId(linha);
	const profissional = profissionalId(linha);
	if (!contrato && !profissional) {
		return erro(
			'Não foi possível identificar o profissional selecionado. Atualize a grade.',
		);
	}

	const response = await requestAbrirAmbientePreenchimentoEscala({
		token,
		scope,
		signal,
		modo: 'detalhado',
		incluirProfissionaisSemCards: true,
		forceRefresh: true,
		contexto: {
			...contexto,
			// O modal precisa reunir os setores do vínculo, independentemente do
			// filtro visual que estava aplicado na grade.
			setor: '',
			vinculo_profissional_id: contrato || undefined,
			profissional_id: contrato ? undefined : profissional,
		},
	});
	if (response?.error) return response;

	const linhasResposta = Array.isArray(response?.linhas)
		? response.linhas
		: Array.isArray(response?.ambiente?.linhas)
			? response.ambiente.linhas
			: response?.linha
				? [response.linha]
				: [];
	const linhasDoAlvo = linhasResposta.filter((item) => {
		if (contrato) return vinculoId(item) === contrato;
		return profissionalId(item) === profissional;
	});

	if (linhasDoAlvo.length === 0) {
		return erro(
			'O ambiente detalhado não retornou as informações do profissional selecionado.',
		);
	}

	return {
		...response,
		linhas: linhasDoAlvo,
		cadastro:
			response?.cadastro ||
			response?.profissional ||
			linhasDoAlvo[0]?.profissional ||
			null,
	};
};

export const detalharLinhasEscala = async ({ token, scope, contexto, linhas, signal, cache }) => {
	const referencias = [...new Map((linhas || []).filter(Boolean).map((linha) => [JSON.stringify([setorId(linha), linha.linha_id || linha.regime_vinculo_id]), linha])).values()];
	const resultado = await Promise.all(referencias.map(async (linha) => {
		const setor = setorId(linha);
		if (!setor || (!linha.linha_id && !linha.regime_vinculo_id)) return erro('Não foi possível identificar a linha e o setor deste registro. Atualize a grade.');
		const args = {
			token, scope, signal, contexto: { ...contexto, setor },
			...(linha.regime_vinculo_id ? { regimeVinculoId: linha.regime_vinculo_id } : { linhaId: linha.linha_id }),
		};
		const carregar = () => requestDetalharLinhaAmbienteEscala(args);
		const seletor = linha.regime_vinculo_id ? ['regime', linha.regime_vinculo_id] : ['linha', linha.linha_id];
		return cache ? cache.ler('linha', JSON.stringify([token, scope, args.contexto, seletor]), carregar) : carregar();
	}));
	const falha = resultado.find((item) => item?.error || !item?.linha);
	if (falha) return falha.error ? falha : erro('O backend não retornou o detalhe solicitado.');
	return { linhas: resultado.map((item) => item.linha) };
};

export const carregarContextosContratoEscala = async ({ token, scope, contexto, linha, signal, cache }) => {
	const contrato = vinculoId(linha);
	if (!contrato) {
		const detalhe = await detalharLinhasEscala({ token, scope, contexto, linhas: [linha], signal, cache });
		return { ...detalhe, cadastro: null, alocacoes: [] };
	}
	const contextoContrato = { ...contexto };
	delete contextoContrato.setor;
	const consultarCadastro = () => requestDetalharVinculoProfissionalEstabelecimento({
		token,
		scope,
		signal,
		id: contrato,
	});
	const consultarContextos = () => requestAbrirAlocacaoOrigemAmbienteEscala({ token, scope, signal, contexto: contextoContrato, vinculoProfissionalId: contrato, forceRefresh: true });
	const chave = JSON.stringify([token, scope, contextoContrato, contrato]);
	const [cadastros, estrutura] = await Promise.all([
		cache ? cache.ler('vinculo', chave, consultarCadastro) : consultarCadastro(),
		cache ? cache.ler('origem', chave, consultarContextos) : consultarContextos(),
	]);
	if (cadastros?.error || estrutura?.error) return cadastros?.error ? cadastros : estrutura;
	const cadastro = cadastros;
	if (!cadastro) return erro('O contrato selecionado não está disponível para edição. Atualize a consulta.');
	if (!Array.isArray(estrutura?.contextos_operacionais)) return erro('O backend ainda não disponibiliza os contextos operacionais deste contrato.');
	const contextos = estrutura.contextos_operacionais.map((item) => ({ ...item.linha, capacidades_contexto: item.capacidades }));
	const detalhes = await detalharLinhasEscala({ token, scope, contexto, linhas: contextos, signal, cache });
	if (detalhes?.error) return detalhes;
	return {
		...estrutura, cadastro,
		linhas: detalhes.linhas.map((detalhe, index) => ({
			...detalhe,
			capacidades_contexto: contextos[index]?.capacidades_contexto,
			profissional: { ...detalhe.profissional, ...cadastro, id: detalhe.profissional?.id || cadastro.id },
		})),
	};
};
